import { ECartTypeId } from '@ringana/configs';
import { ErrorCodes } from '@ringana/errors';
import CartHelper from '@/helpers/CartHelper';
import CustomerHelper from '@/helpers/CustomerHelper';
import config from '../../config';
import CookieHelper from './CookieHelper';
import FetchHelper from './OldFetchHelper';
import LocalStorageHelper from './OldLocalStorageHelper';
import OldErrorHandlerHelper from './OldErrorHandlerHelper';
import DomEventHelper from './DomEventHelper';

class OldCartHelper {
  constructor() {
    window.cartRetriedLoadingOnce = false;
    window.Ringana = window.Ringana ? window.Ringana : {};
    window.Ringana.addCartItem = this.addItemToCart;
    window.Ringana.language = CookieHelper.getLanguage();
    if(LocalStorageHelper.isCartToBeReloaded()) {
      LocalStorageHelper.removeCartToBeReloaded();
      // can't reload directly. have to wait for the circular dependency
      setTimeout(() => this.fetchCart(), 500);
    }
  }

  async getCarts() {
    await this.loadOrFetchCart();
    if(!this.cart) {
      const bouncerActive = await FetchHelper.get({ path: `${config.api.paths.configs.basepath}${config.api.paths.configs.paths.bouncer}` });
      if(bouncerActive.data.active) {
        this.cart = {
          currency: `EUR`,
          deliveryAddress: {
            city: null,
            deliveryDefault: false,
            receiptDefault: false,
            recipient: null,
            street: null,
            streetNumber: null,
            zipCode: null,
          },
          deliveryType: {
            cost: 0,
            id: -1,
            name: null,
          },
          shoppingCarts: [
            {
              availableSets: [],
              cartId: -1,
              cartTypeId: config.cartTypes.EIGENBEDARF,
              cn: -1,
              coupons: [],
              deliveryAddress: {
                city: null,
                deliveryDefault: false,
                receiptDefault: false,
                recipient: null,
                street: null,
                streetNumber: null,
                zipCode: null,
              },
              deliveryType: null,
              deliveryTypesInfo: null,
              invoiceAddress: {
                city: null,
                deliveryDefault: false,
                receiptDefault: false,
                recipient: null,
                street: null,
                streetNumber: null,
                zipCode: null,
              },
              items: [],
              itemsCount: 0,
              name: null,
              partner: false,
              paymentTerm: {
                id: -1,
                name: null,
                type: null,
              },
              paymentType: null,
              specialOffers: [],
              specialOffersInfo: null,
              sum: {
                open: 0,
                points: 0,
                postage: 0,
                subtotal: 0,
                total: 0,
                vat: 0,
              },
              supplements: [],
              taxNumber: null,
              taxNumberStatus: null,
            },
          ],
          sum: {
            open: 0,
            points: 0,
            postage: 0,
            subtotal: 0,
            total: 0,
            vat: 0,
          },
          totalItemsCount: 0,
        };
      }
    }

    return this.cart?.shoppingCarts ?? [];
  }

  async getCartsForCheckout(cartIds) {
    await this.fetchCart(cartIds);
    return this.cart?.shoppingCarts ?? [];
  }

  async hasMultipleCarts() {
    await this.loadOrFetchCart();
    return this.cart.shoppingCarts.length > 1;
  }

  async fetchDeliveryAddresses(cartId = null, mergeAddresses = []) {
    const fetchConfig = {
      body: { cartId: cartId || CookieHelper.getLastUsedCartId() },
      path: `${config.api.paths.checkout.basepath}${config.api.paths.checkout.paths.addresses}`,
    };

    const partnerShopInfo = LocalStorageHelper.getPartnerShopInformation();
    if(partnerShopInfo) {
      fetchConfig.body.partnerShop = partnerShopInfo.customerNumber.toString();
    }

    const fetchResult = await FetchHelper.post(fetchConfig);
    if(fetchResult.success) {
      const addresses = fetchResult.data;
      addresses.addresses = [...mergeAddresses, ...addresses.addresses];
      LocalStorageHelper.setDeliveryAddresses(addresses);
      return addresses;
    }
    OldErrorHandlerHelper.handleError(fetchResult, { logToConsole: true });
    throw new Error(fetchResult.message);
  }

  /**
   * @param partnerId
   *
   * @returns {Promise<{ info: 'addCartCustomerEmailEmpty' | undefined, cartId: number, cartTypeId: number } | null>} returns the information if the customer email is empty
   */
  async createCustomerCart(partnerId) {
    const fetchConfig = {
      body: {
        customerNumber: +partnerId,
      },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.create}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      this.lastUsedCartId = fetchResult.data.cartId;
      CookieHelper.setLastUsedCartId(this.lastUsedCartId);
      CookieHelper.setCartSession(fetchResult.data.session);
      await this.fetchCart();
      return {
        cartId: fetchResult.data.cartId,
        cartTypeId: fetchResult.data.typeId,
        info: fetchResult.data.info,
      };
    }
    if(fetchResult.error === ErrorCodes.INFOWORX_RESPONSE_EMAIL_MISSING) {
      throw new Error(`cart:emailMissing`);
    }
    throw new Error(`cart:customerCartNotCreated`);
  }

  async deleteCustomerCart(cartId, cartTypeId) {
    const fetchConfig = {
      body: {
        cartId,
      },
      path: `${config.api.paths.carts.basepath}`,
    };

    const fetchResult = await FetchHelper.delete(fetchConfig);

    if(fetchResult.success) {
      // recreate the Eigenbedarf cart if it was deleted
      if(cartTypeId === config.cartTypes.EIGENBEDARF) {
        await this.createNewCart();
      }
      CookieHelper.removeCartId();
      await this.fetchCart();
    } else {
      throw new Error();
    }
  }

  async createNewCart() {
    const fetchConfig = {
      body: { type: ECartTypeId.PERSONAL_USE },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.create}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      this.lastUsedCartId = fetchResult.data.cartId;
      CookieHelper.setLastUsedCartId(this.lastUsedCartId);
      CookieHelper.setPersonalUseCartId(this.lastUsedCartId);
      CookieHelper.setCartSession(fetchResult.data.session);
      await this.fetchCart();
    }
  }

  setCart(newCart) {
    this.cart = newCart;
    let itemsCount = 0;

    for(let i = 0; i < this.cart.shoppingCarts.length; i++) {
      this.cart.shoppingCarts[i].items = _.sortBy(this.cart.shoppingCarts[i].items, `title`);
      if(this.cart.shoppingCarts[i].cartTypeId !== config.cartTypes.PARTNERAPPLICATION) {
        itemsCount += this.cart.shoppingCarts[i].itemsCount;
      }
    }

    CookieHelper.setLastUsedCartId(newCart.shoppingCarts[0].cartId);
    LocalStorageHelper.setCart(newCart);
    CartHelper.updateCartCount(itemsCount);
  }

  async loadOrFetchCart() {
    // cart already loaded? here you are
    if(this.cart) {
      return this.cart;
    }

    // not loaded? local storage, can you help me plz?
    this.cart = LocalStorageHelper.getCart();

    // still did got get a cart?
    if(!this.cart) {
      await this.fetchCart();
    }

    // no? ok, well then sudo get me a cart
    if(!this.cart) {
      CookieHelper.removeCart();
      await this.createNewCart();
    }

    return this.cart;
  }

  /**
   * @param cartIds {number[]} defaults to empty array
   * @returns {Promise<void>}
   */
  async fetchCart(cartIds = []) {
    // do not fetch cart if you cannot possibly have one
    if(!CookieHelper.getCartSession() && !CookieHelper.getAuthToken()) return;
    const fetchConfig = {
      path: `${config.api.paths.carts.basepath}/`,
    };

    if(cartIds.length > 0) {
      fetchConfig.path += cartIds.toString();
    }

    const fetchResult = await FetchHelper.get(fetchConfig);

    if(fetchResult.success) {
      const cart = fetchResult.data;

      if(!cart) {
        // we deleted the last cart, please create a new one (partners only!)
        await this.createNewCart();
      } else {
        // party
        this.setCart(cart);
      }
    }
  }

  async removeItemFromCart(cartContentId, isPartnerApplication, cartId, type) {
    const fetchConfig = {
      body: {
        cartContentId,
        cartId,
        isPartnerApplication,
        type,
      },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.item}`,
    };

    const fetchResult = await FetchHelper.delete(fetchConfig);

    if(fetchResult.success) {
      CartHelper.updateLocalCart(fetchResult.data.shoppingCarts[0]);
      return fetchResult.data;
    }
    return fetchResult.message;
  }

  async updateQuantity(item, isPartnerApplication = false, swal) {
    if(item.amount < 1) {
      return this.removeItemFromCart(item.cartContentId, isPartnerApplication);
    }

    const fetchConfig = {
      body: {
        amount: item.amount,
        cartContentId: item.cartContentId,
        isPartnerApplication,
        matchcode: item.matchcode,
      },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.item}`,
    };

    const fetchResult = await FetchHelper.patch(fetchConfig);

    if(fetchResult.success) {
      CartHelper.updateLocalCart(fetchResult.data.shoppingCarts[0]);
      return fetchResult.data;
    }
    if(swal && fetchResult.message) {
      if(new RegExp(`^Dieser Artikel kann maximal `, `ig`).test(fetchResult.message)) {
        const regexp = /^Dieser Artikel kann maximal ([0-9]*)-/ig;
        const dataArr = regexp.exec(fetchResult.message);
        await window.$swal(``,
          `${window.vueCartHelperTexts.errorMaxUnitsPerCart.part1}${dataArr[1]}${window.vueCartHelperTexts.errorMaxUnitsPerCart.part2}`,
          `error`);
        return false;
      }
      swal(``, fetchResult.message, `warning`);
      return false;
    }
    return false;
  }

  async fetchCoupons(cn = null, genericAutoredeem = false) {
    let query = (cn !== null && cn !== ``) ? `?customerNumber=${cn}` : ``;
    query += `${query.length > 0 ? `&` : `?`}genericAutoredeem=${genericAutoredeem}`;
    const fetchConfig = {
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.coupons}${query}`,
    };
    return FetchHelper.get(fetchConfig);
  }

  async redeemCoupon(cartId, code) {
    const fetchConfig = {
      body: { cartId, code },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.coupon}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      return fetchResult.data;
    }
    return fetchResult.message;
  }

  async removeCoupon(cartContentId, cartId, code) {
    const fetchConfig = {
      body: {
        cartContentId,
        cartId,
        code,
        type: config.cartItemType.coupon,
      },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.coupon}`,
    };

    const fetchResult = await FetchHelper.delete(fetchConfig);

    if(fetchResult.success) {
      return fetchResult.data;
    }
    return fetchResult.message;
  }

  async addItemToCart(body, cartId, cartTypeId) {
    let internalCartId = cartId;

    // no cart yet? create one!
    if(!CookieHelper.getCartSession() && !CookieHelper.getAuthToken()) {
      await this.createNewCart();
      internalCartId = CookieHelper.getLastUsedCartId();
    }

    if(!internalCartId || typeof internalCartId === `undefined` || +internalCartId <= 0) {
      internalCartId = CookieHelper.getLastUsedCartId();
    } else {
      switch(cartTypeId) {
        case config.cartTypes.EIGENBEDARF:
          CookieHelper.setPersonalUseCartId(internalCartId);
          await CookieHelper.setLastUsedCartId(internalCartId);
          break;
        case config.cartTypes.FREMDBEDARF:
          await CookieHelper.setLastUsedCartId(internalCartId);
          break;
        case config.cartTypes.PARTNERAPPLIKATION:
          // do nothing, PA is not important enough to be stored
          break;
        default:
          console.warn(`cartTypeId '${cartTypeId}' unknown!`);
      }
    }

    const fetchConfig = {
      body: { ...body, cartId: internalCartId },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.item}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      CookieHelper.setCartSession(fetchResult.session);
      CartHelper.updateLocalCart(fetchResult.data.shoppingCarts[0]);

      $(`.cartIcon`).addClass(`shake is-animating`);
      setTimeout(() => $(`.cartIcon`).removeClass(`shake is-animating`), 200);

      // retry enabled again
      this.cartRetriedLoadingOnce = false;

      if(CustomerHelper.getLoggedInState()) {
        const updatedCart = fetchResult.data.shoppingCarts[0];
        let firstBuyerProductWithCoupon = null;
        if(updatedCart) {
          const coupons = await this.fetchCoupons(updatedCart.cn, true);
          if(coupons.success) {
            for(const item of updatedCart.items) {
              const couponWithMatchcode = coupons.data.find(coupon => coupon.matchcode === item.matchcode);
              if(couponWithMatchcode) {
                firstBuyerProductWithCoupon = {
                  coupon: couponWithMatchcode,
                  item,
                  updatedCart,
                };
              }
            }
          }
        }

        if(firstBuyerProductWithCoupon) {
          await this.removeItemFromCart(firstBuyerProductWithCoupon.item.cartContentId, false, firstBuyerProductWithCoupon.updatedCart.cartId, firstBuyerProductWithCoupon.updatedCart.cartTypeId);
          await this.redeemCoupon(firstBuyerProductWithCoupon.updatedCart.cartId, firstBuyerProductWithCoupon.coupon.code);
          DomEventHelper.fireEvent({ name: `rin-add-autoredeemed-coupon` });
        }
      }
    } else if(!fetchResult.success && new RegExp(`^Dieser Artikel kann maximal `, `ig`).test(fetchResult.message)) {
      // TODO - totally refactoring with errorcodes from infoworx !!!
      const regexp = /^Dieser Artikel kann maximal ([0-9]*)-/ig;
      const dataArr = regexp.exec(fetchResult.message);
      await window.$swal(``,
        `${window.vueCartHelperTexts.errorMaxUnitsPerCart.part1}${dataArr[1]}${window.vueCartHelperTexts.errorMaxUnitsPerCart.part2}`,
        `error`);
    } else if(fetchResult.error === config.api.errorCodes.requestsLimited) {
      DomEventHelper.fireEvent({ name: `rin-limiting-active` });
      DomEventHelper.fireEvent({ name: `rin-limiting-shopping-disallowed` });
      DomEventHelper.fireEvent({ detail: fetchConfig.body, name: `rin-limiting-add2cart-retry` });
    } else {
      OldErrorHandlerHelper.handleError(fetchResult, { logToConsole: true });
      // TODO : Refactor to errorCodes (ticket created WEB-787)
      /* eslint-disable max-len */
      switch(fetchResult.message) {
        case `<idwarenkorb> invalid`:
          /**
           * cart not known or invalid - try again, lets fetch a new cart
           */
          CookieHelper.removeCartId();
          CookieHelper.removeCartSession();
          CookieHelper.removeCartIdsForCheckout();
          if(!this.cartRetriedLoadingOnce) {
            this.cartRetriedLoadingOnce = true;
            await this.createNewCart();
            await this.addItemToCart(body);
          } else {
            await window.$swal(``, window.vueCartHelperTexts.errorCannotBeAddedTemporary, `error`);
            window.location.reload();
          }
          break;
        case `product cannot be bought`:
          await window.$swal(``, window.vueCartHelperTexts.errorCannotBeBoughtTemporary, `error`);
          break;
        default:
          if(fetchResult.error === 11018) {
            await window.$swal(``, fetchResult.message, `error`);
          } else {
            await window.$swal(``, window.vueCartHelperTexts.errorUnknown, `error`);
          }
      }
      /* eslint-enable max-len */
    }
    return fetchResult;
  }

  async addSupplement(cartId, supplementId, isPartnerApplication = false) {
    const fetchConfig = {
      body: { cartId, isPartnerApplication, supplementId },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.supplement}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      return fetchResult.data;
    }
    window.$swal(``, window.vueCartHelperTexts.errorUnknown, `error`);
    return fetchResult.message;
  }

  async placeSetInCart(matchcode, cartId) {
    const fetchConfig = {
      body: { cartId, matchcode, setSwap: true },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.item}`,
    };

    const fetchResult = await FetchHelper.post(fetchConfig);

    if(fetchResult.success) {
      return fetchResult.data;
    }
    window.$swal(``, window.vueCartHelperTexts.errorUnknown, `error`);
    return fetchResult.message;
  }

  async updateItem(body, cartContentId) {
    const fetchConfig = {
      body: { ...body, cartContentId },
      path: `${config.api.paths.carts.basepath}${config.api.paths.carts.paths.item}`,
    };

    const fetchResult = await FetchHelper.patch(fetchConfig);

    if(!fetchResult.success) {
      throw new Error(fetchResult.message);
    }

    return fetchResult.data;
  }

  mergeModifiedCartIntoExistingCarts(modifiedCart) {
    if(!modifiedCart) {
      return;
    }
    const cart = modifiedCart.shoppingCarts[0];
    const indexOfExistingCartToBeModified = this.cart.shoppingCarts.findIndex(shoppingCart => shoppingCart.cartId === cart.cartId);
    if(indexOfExistingCartToBeModified !== -1) {
      this.cart.shoppingCarts.splice(indexOfExistingCartToBeModified, 1, cart);
      return;
    }
    this.cart.push(modifiedCart);
  }

  setTaxNumberForOwnCart(taxNumber) {
    for(const cart of this.cart.shoppingCarts) {
      if(cart.cartTypeId === ECartTypeId.PERSONAL_USE) {
        cart.taxNumber = taxNumber;
      }
    }
  }
}

export default new OldCartHelper();
